// ** React Imports
import { useState, useEffect, Fragment, memo } from 'react'
import { useNavigate } from 'react-router-dom';

// ** MUI Imports
import Box from '@mui/material/Box'
import Menu from '@mui/material/Menu'
import Badge from '@mui/material/Badge'
import Avatar from '@mui/material/Avatar'
import Divider from '@mui/material/Divider'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import MenuItem from '@mui/material/MenuItem'
import UnsavedChangesDialog from "components/UnsavedChangesDialog";
import { useUnsavedChanges } from "components/UnsavedChangesContext";

// ** Icon Imports
import Icon from '../../../components/icon'

// ** Context
import useAuth from '../../../hooks/useAuth'
import { getUser } from 'utils/auth';
import { useSettings } from 'components/hooks/useSettings'
import { VersionIcon } from 'components/versionbadge';
import { WebVersionChip } from 'components/customer_version_icon';
import { AnimatedVersion } from 'components/animated_version_badge';
import packageJson from '../../../../package.json';
import { GetOnboardingStatus } from 'components/logic/paymentgateway/GetOnboardingStatus';

const { version } = packageJson;

// ** Styled Components
const BadgeContentSpan = styled('span')(({ theme }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: theme.palette.success.main,
  boxShadow: `0 0 0 2px ${theme.palette.background.paper}`
}))

const MenuItemStyled = styled(MenuItem)(({ theme }) => ({
  '&:hover .MuiBox-root, &:hover .MuiBox-root svg': {
    color: theme.palette.primary.main
  }
}))

//const version = process.env.REACT_APP_VERSION;

const UserDropdown = memo(() => {
  // ** Props
  const { settings } = useSettings();

  // ** States
  const [anchorEl, setAnchorEl] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  // ** Hooks
  const { logout } = useAuth();
  const navigate = useNavigate();
  
  // ** Vars
  const { direction } = settings
  //const User = getUser();
 const [tabValue, setTabValue] = useState("1");
  const [isChanged, setIsChanged] = useState(false);
  const [User, setUser] = useState({});
    const { setHasUnsavedChanges, hasUnsavedChanges } = useUnsavedChanges();
     const [openUnsavedChangesDlg, setOpenUnsavedChangesDlg] = useState(false);
     const [unsavedDialogAction, setUnsavedDialogAction] = useState({
        action: '',
        tabValue: '',
      });
       const handleOpenUnsavedChangesDlg = (action, tabValue) => {
        if(action === 'tabChange'){
          setUnsavedDialogAction({
            action: action,
            tabValue: tabValue,
          });
        }else{
          setUnsavedDialogAction({
            action: action,
            tabValue: '',
          });
        }
        setOpenUnsavedChangesDlg(true); 
      }
      const handleCloseUnsavedChangesDlg = () => setOpenUnsavedChangesDlg(false);
      const handleDiscardChanges = () => { 
         setHasUnsavedChanges(false);
          setOpenUnsavedChangesDlg(false); 
          navigate('/login')
        }
      

  const styles = {
    px: 4,
    py: 1.75,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    color: 'text.primary',
    textDecoration: 'none',
    '& svg': {
      mr: 2.5,
      fontSize: '1.5rem',
      color: 'text.secondary'
    }
  }

  const getInitials = (name) => {
    if (!name) return "";
    const words = name.split(" ");
    return words.map((word) => word[0]).join("").toUpperCase();
  };

  // Check for the JWT token
  useEffect(() => {
    const token = localStorage.getItem('jwtToken');
    if (!token) {
      navigate('/login');
    } else {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const getUserData = async()=>{
      const response = await GetOnboardingStatus(navigate);
      if(!response.isError){
        setUser(response.result);
      }else{
        setUser({});
        console.error("Error fetching user data:", response.message);
      }
    }
    getUserData();

  },[User?.fullName]);

  const handleDropdownOpen = event => {
    setAnchorEl(event.currentTarget);
  }

  const handleDropdownClose = () => {
    setAnchorEl(null);
  }
 
  const handleNavigation = (to) => {              
    if(isChanged){
       handleOpenUnsavedChangesDlg('navigate');
        }else{
      navigate(to);
    }
  };
  
  const handleLogout = (event) => {
    event.preventDefault();
    if (hasUnsavedChanges) {
        handleOpenUnsavedChangesDlg('navigate');
        }else{
          setHasUnsavedChanges(false);
          //navigate('/login');
          logout();
          window.location.replace('/login');
      }
  };

  return (
    <Fragment>
      <Badge
        overlap='circular'
        sx={{ ml: 2, cursor: 'pointer' }}
        badgeContent={<BadgeContentSpan />}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
      >
        <Avatar
          alt={User && User.fullName ? User.fullName : 'Guest'}
          src=''
          onClick={handleDropdownOpen}
          sx={{ width: 38, height: 38, bgcolor: 'rgb(0 58 112)', color:'white'}}
        >
          {User && User?.fullName ? getInitials(User?.fullName) : "G"}
        </Avatar>
      </Badge>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleDropdownClose}
        sx={{ '& .MuiMenu-paper': { width: 230, mt: 4.75 } }}
        anchorOrigin={{ vertical: 'bottom', horizontal: direction === 'ltr' ? 'right' : 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: direction === 'ltr' ? 'right' : 'left' }}
      >
        <Box sx={{ py: 1.75, px: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Badge
              overlap='circular'
              badgeContent={<BadgeContentSpan />}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right'
              }}
            >
              <Avatar 
                alt={User && User.fullName ? User.fullName : 'Guest'} 
                src='' 
                sx={{ width: '2.5rem', height: '2.5rem', bgcolor: 'rgb(0 58 112)', color:'white'}}
              >
                {User && User?.fullName ? getInitials(User?.fullName) : "G"}
              </Avatar>
            </Badge>
            <Box sx={{ display: 'flex', ml: 2.5, alignItems: 'flex-start', flexDirection: 'column' }}>
              <Typography sx={{ fontWeight: 500 }}>{User && User.fullName ? User.fullName : 'Guest'}</Typography>
              <Typography variant='body2'>
                {User && User.roleName ? User.roleName : ""}{User && User.teamName ? ' • '+User.teamName : ''}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Divider sx={{ my: theme => `${theme.spacing(2)} !important` }} />
        <MenuItemStyled sx={{ p: 0 }} onClick={() => {
          handleDropdownClose();
          navigate('/profile');
        }}>
          <Box sx={styles}>
            <Icon icon='tabler:user-check' />
            My Profile
          </Box>
        </MenuItemStyled>
        <MenuItemStyled sx={{ p: 0 }} onClick={() => {
          handleDropdownClose();
          navigate('/settings');
        }}>
          <Box sx={styles}>
            <Icon icon='tabler:settings' />
            Settings
          </Box>
        </MenuItemStyled>
        <Divider sx={{ my: theme => `${theme.spacing(2)} !important` }} />
        <MenuItemStyled sx={{ p: 0 }} onClick={() => {
          handleDropdownClose();
          navigate('/help');
        }}>
          <Box sx={styles}>
            <Icon icon='tabler:lifebuoy' />
            Help
          </Box>
        </MenuItemStyled>
        <MenuItemStyled sx={{ p: 0 }} onClick={() => {
          handleDropdownClose();
          navigate('/faq');
        }}>
          <Box sx={styles}>
            <Icon icon='tabler:info-circle' />
            FAQ
          </Box>
        </MenuItemStyled>
        <Divider sx={{ my: theme => `${theme.spacing(2)} !important` }} />
        <MenuItemStyled sx={{ p: 0 }} onClick={handleLogout}>
          <Box sx={styles}>
            <Icon icon='tabler:logout' />
            Log Out
          </Box>
        </MenuItemStyled>
        <Divider sx={{ my: theme => `${theme.spacing(2)} !important` }} />
        <MenuItemStyled sx={{ p: 0 }}>
          <Box sx={styles}>            
            {/* Version : 
            <Typography variant="caption" sx={{ ml: 1, color: 'text.disabled', fontSize: '1rem', fontFamily: 'monospace' }}>
              v{version}
            </Typography>
             */}
             {/* <VersionIcon version={version} /> */}
             {/* <WebVersionChip version={version}  /> */}
             <AnimatedVersion version={version} isNew={false}/>
          </Box>
        </MenuItemStyled>
         <UnsavedChangesDialog open={openUnsavedChangesDlg} handleClose={handleCloseUnsavedChangesDlg} handleDiscard={handleDiscardChanges} />
      </Menu>
    </Fragment>
    
  )
})

UserDropdown.displayName = 'UserDropdown';
export default UserDropdown